/**
 * V70.40: 조직명 표시 순서 공통 comparator
 *
 *  `조직별 인력 구성` 과 `조직별 계약 결과` 가 **같은 기준**으로 정렬되도록
 *  정렬 로직을 이 파일 하나에만 둔다 (두 테이블에 중복 구현 금지).
 *
 *  정렬 규칙
 *    1) 조직명 **시작 문자 category rank** 로 먼저 묶는다
 *         한글(0) → 영문(1) → 숫자(2) → 기타(3)
 *       → 숫자로 시작하는 조직이 한글 조직 사이에 섞이지 않는다.
 *    2) 같은 category 안에서는 한국어 locale + natural sort
 *         `Intl.Collator('ko', { numeric: true })`
 *       → `2팀 < 10팀` (문자열 비교의 `10팀 < 2팀` 방지)
 *       → 이름 중간 숫자에도 적용된다 (`플랫폼QA2팀 < 플랫폼QA10팀`)
 *
 *  영문 category 위치 근거
 *    실제 데이터(QA/Prod) 의 조직명 시작 문자는 **한글과 영문뿐**이고
 *    (영문 = 회사 루트 조직 `NGLE`, `NGLE_대련지사`) 숫자 시작 조직은 현재 없다.
 *    숫자 시작 조직명(`1사업팀`, `2사업팀` …)은 열거형 계열로 읽히므로 맨 뒤에 두고,
 *    이름형인 영문은 한글 바로 뒤에 둔다. 데이터가 늘어도 순서가 흔들리지 않는다.
 *
 *  ⚠️ 이 모듈은 **표시 순서만** 바꾼다. 집계값·비율·컬럼 구성에는 관여하지 않는다.
 */

/** 조직명 시작 문자 category rank (작을수록 앞) */
export function orgNameCategoryRank(name: string): number {
  const ch = (name || '').trim().charAt(0);
  if (!ch) return 4;                                        // 이름 없음 → 맨 뒤
  if (/[가-힣ㄱ-ㆎ]/.test(ch)) return 0;    // 한글 (완성형 + 자모)
  if (/[A-Za-z]/.test(ch)) return 1;                        // 영문
  if (/[0-9]/.test(ch)) return 2;                           // 숫자
  return 3;                                                 // 기타/특수문자
}

/**
 * 한국어 locale + natural sort collator.
 *  numeric: true 가 `2 < 10` 을 보장한다.
 *  Intl 미지원 환경에서는 localeCompare 로 안전하게 되돌린다.
 */
const collator: { compare: (a: string, b: string) => number } = (() => {
  try {
    return new Intl.Collator('ko', { numeric: true, sensitivity: 'variant' });
  } catch {
    return { compare: (a: string, b: string) => a.localeCompare(b, 'ko') };
  }
})();

/** 조직명 비교 — category rank 우선, 같은 category 안에서 ko + natural sort */
export function compareOrganizationNames(a: string, b: string): number {
  const ra = orgNameCategoryRank(a);
  const rb = orgNameCategoryRank(b);
  if (ra !== rb) return ra - rb;
  return collator.compare(a || '', b || '');
}

/**
 * 조직 행 배열을 표시 순서로 정렬한 **새 배열**을 반환한다.
 *  원본을 변형하지 않는다 (집계 결과 객체를 그대로 재사용하므로 값은 바뀌지 않는다).
 */
export function sortOrgRowsByName<T extends { orgUnitName: string }>(rows: readonly T[]): T[] {
  return [...rows].sort((x, y) => compareOrganizationNames(x.orgUnitName, y.orgUnitName));
}
